import { useState, useEffect } from 'react'
import { X, MapPin, Route, Mountain, Clock, Check, UserPlus, Lock, Key } from 'lucide-react'
import { RouteVisibility } from '@cya-routes/shared'
import { BookmarkButton } from '../common/BookmarkButton'
import { getElevationGradientLevel, getElevationGradientColor, getElevationGradientLabel } from '../../services/elevationGradientService'

interface RouteModalData {
  name: string
  routeType: string
  coordinates: [number, number][]
  location: string
  region: string
  distance: number
  elevationGain: number
  surface: string
  unpavedPercentage: number
  description: string
  creator: string
  creatorPhoto?: string | null
  organization: string
  clubLogo?: string | null
  images: string[]
  thumbnail?: string | null
  firebaseRoute?: any
  visibility?: RouteVisibility
  attribution?: {
    name: string
    email?: string
  }
}

interface RoutePopupCardProps {
  route: RouteModalData | null
  onClose: () => void
  onViewFullRoute?: (route: any) => void
  userMemberships?: Array<{ clubId: string; status: string; role: string }>
  embedConfig?: {
    links?: {
      useDefaultLinks: boolean
      customLinkText?: string
      showBothLinks?: boolean
      routeLinks?: { [routeId: string]: string }
    }
    routes?: {
      [routeId: string]: {
        color?: string
        customLink?: string
        tags?: string[]
      }
    }
  }
}

// Generate static map URL for routes without images
const generateStaticMapUrl = (route: RouteModalData): string => {
  // Use Mapbox static API
  const accessToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN
  
  // Get coordinates for the route path
  let coordinates: [number, number][] = []
  
  if (route.firebaseRoute?.routes && route.firebaseRoute.routes.length > 0) {
    // For multi-segment routes, use the first segment's coordinates
    const firstRoute = route.firebaseRoute.routes[0]
    if (firstRoute.coordinates && firstRoute.coordinates.length > 0) {
      // Sample coordinates to avoid URL length limits
      const step = Math.max(1, Math.floor(firstRoute.coordinates.length / 100))
      coordinates = firstRoute.coordinates.filter((_: any, index: number) => index % step === 0)
    }
  } else if (route.coordinates && route.coordinates.length > 0) {
    // For single routes, use the coordinates directly
    const step = Math.max(1, Math.floor(route.coordinates.length / 100))
    coordinates = route.coordinates.filter((_, index) => index % step === 0)
  }
  
  if (coordinates.length > 0) {
    // Create polyline for the route
    const polyline = coordinates.map(coord => `${coord[0]},${coord[1]}`).join(',')
    
    // Calculate bounds
    const lngs = coordinates.map(c => c[0])
    const lats = coordinates.map(c => c[1])
    const minLng = Math.min(...lngs)
    const maxLng = Math.max(...lngs)
    const minLat = Math.min(...lats)
    const maxLat = Math.max(...lats)
    
    const url = `https://api.mapbox.com/styles/v1/mapbox/outdoors-v12/static/path-2+ef4444(${polyline})/[${minLng},${minLat},${maxLng},${maxLat}]/640x480@2x?access_token=${accessToken}`
    console.log('🗺️ Generated static map URL:', url)
    return url
  }
  
  // Fallback to a default outdoor image
  console.log('⚠️ No coordinates for static map, using fallback')
  return 'https://api.mapbox.com/styles/v1/mapbox/outdoors-v12/static/144.9631,-37.8136,10/640x480@2x?access_token=' + accessToken
}

export function RoutePopupCard({ route, onClose, onViewFullRoute, userMemberships = [], embedConfig }: RoutePopupCardProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [imagesLoaded, setImagesLoaded] = useState<boolean[]>([])
  const [viewportScale, setViewportScale] = useState(1)

  // Update viewport scale based on window size
  useEffect(() => {
    const updateScale = () => {
      // Responsive scaling based on screen width
      let scale = 0.85 // Default scale for desktop
      
      if (window.innerWidth < 640) { // Mobile phones
        scale = 0.6
      } else if (window.innerWidth < 768) { // Small tablets
        scale = 0.7
      } else if (window.innerWidth < 1024) { // Tablets
        scale = 0.75
      }
      
      setViewportScale(scale)
      console.log('🔍 RoutePopupCard scale:', scale, 'window width:', window.innerWidth)
    }

    updateScale()
    window.addEventListener('resize', updateScale)
    return () => window.removeEventListener('resize', updateScale)
  }, [])

  // Debug log
  useEffect(() => {
    if (route) {
      console.log('🎴 RoutePopupCard received route:', {
        name: route.name,
        hasImages: route.images.length > 0,
        imagesCount: route.images.length,
        images: route.images,
        thumbnail: route.thumbnail,
        hasThumbnail: !!route.thumbnail
      })
    }
  }, [route])

  // Reset state when route changes
  useEffect(() => {
    if (route?.images && route.images.length > 0) {
      // Reset loaded state
      setImagesLoaded(new Array(route.images.length).fill(false))
      setCurrentImageIndex(0)
    }
  }, [route?.images])

  // Lazy load only the current image and preload the next one
  useEffect(() => {
    if (route?.images && route.images.length > 0 && route.images[currentImageIndex]) {
      // Load the current image
      if (!imagesLoaded[currentImageIndex]) {
        console.log(`🖼️ Lazy loading image ${currentImageIndex + 1}/${route.images.length}`)
        const img = new Image()
        img.onload = () => {
          setImagesLoaded(prev => {
            const newState = [...prev]
            newState[currentImageIndex] = true
            return newState
          })
          console.log(`✅ Image ${currentImageIndex + 1} loaded`)
        }
        img.onerror = () => {
          console.error(`❌ Failed to load image ${currentImageIndex + 1}`)
        }
        img.src = route.images[currentImageIndex]
      }
      
      // Preload the next image for smoother navigation
      const nextIndex = (currentImageIndex + 1) % route.images.length
      if (!imagesLoaded[nextIndex] && route.images[nextIndex]) {
        console.log(`🔄 Preloading next image ${nextIndex + 1}`)
        const nextImg = new Image()
        nextImg.onload = () => {
          setImagesLoaded(prev => {
            const newState = [...prev]
            newState[nextIndex] = true
            return newState
          })
          console.log(`✅ Next image ${nextIndex + 1} preloaded`)
        }
        nextImg.src = route.images[nextIndex]
      }
    }
  }, [route?.images, currentImageIndex, imagesLoaded])

  if (!route) return null

  // Check if user is following this club
  const isFollowingClub = userMemberships && userMemberships.length > 0 && userMemberships.some(membership => 
    membership.clubId === (route.submission?.clubId || route.firebaseRoute?.submission?.clubId) && 
    membership.status === 'approved'
  )

  return (
    <div 
      className={`absolute z-50 bg-gradient-to-b from-black/40 to-gray-950/50 backdrop-blur-2xl rounded-xl shadow-2xl shadow-black/50 border border-white/[0.06] overflow-hidden ring-1 ring-inset ring-white/[0.05] before:absolute before:inset-0 before:bg-gradient-to-br before:from-white/[0.02] before:to-transparent before:pointer-events-none
        ${window.innerWidth < 640 
          ? 'fixed top-1/2 right-2 -translate-y-1/2 w-80' 
          : 'absolute top-2 right-12 w-80'
        }`}
      style={{ 
        transform: window.innerWidth < 640 
          ? `translateY(-50%) scale(${viewportScale})` 
          : `scale(${viewportScale})`,
        transformOrigin: window.innerWidth < 640 ? 'right center' : 'top right'
      }}
    >
      {/* Card Header */}
      <div className="relative h-96 bg-gray-700">
        {/* Display logic: 
            1. If images array has photos, show them
            2. If images is empty array AND firebaseRoute has been loaded (meaning we checked for photos), show thumbnail
            3. If images is empty array but firebaseRoute not loaded yet, show loading state
            4. Only show static map if neither images nor thumbnail exist
        */}
        {route.images.length > 0 ? (
          <>
            <img 
              src={route.images[currentImageIndex]} 
              alt={route.name}
              className="w-full h-96 object-cover"
            />
            {/* Show loading overlay while image is loading */}
            {!imagesLoaded[currentImageIndex] && (
              <div className="absolute inset-0 bg-gray-700 flex items-center justify-center">
                <div className="animate-pulse">
                  <svg className="w-12 h-12 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>
            )}
          </>
        ) : route.images.length === 0 && route.firebaseRoute && route.firebaseRoute.photos === undefined ? (
          // Images array is empty and photos haven't been loaded yet - show loading instead of thumbnail
          <div className="w-full h-96 bg-gray-700 flex items-center justify-center">
            <div className="animate-pulse">
              <svg className="w-12 h-12 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
        ) : route.thumbnail ? (
          <img 
            src={route.thumbnail}
            alt={route.name}
            className="w-full h-96 object-cover"
          />
        ) : (
          <img 
            src={(() => {
              console.log('🏞️ Using static map as last resort for route:', route.name)
              return generateStaticMapUrl(route)
            })()}
            alt={route.name}
            className="w-full h-96 object-cover"
          />
        )}
        
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        
        {/* Action Buttons */}
        <div className="absolute top-2 right-2 flex items-center gap-2">
          {/* Bookmark Button */}
          {route.firebaseRoute && (
            <BookmarkButton
              routeId={route.firebaseRoute.id || route.id}
              routeData={{
                routeId: route.firebaseRoute.id || route.id,
                routeName: route.name,
                routeThumbnail: route.thumbnail || route.images[0],
                routeDistance: route.distance,
                routeElevation: route.elevationGain,
                routeLocation: typeof route.location === 'string' 
                  ? route.location 
                  : route.location && typeof route.location === 'object'
                    ? `${route.location.city || route.location.region || ''}, ${route.location.state || ''}, ${route.location.country || ''}`.replace(/^, |, $|, ,/g, '').replace(/^,\s*/, '')
                    : 'Unknown Location',
                routeType: route.routeType,
                clubId: route.firebaseRoute?.submission?.clubId,
                clubName: route.organization
              }}
              size="md"
              variant="overlay"
            />
          )}
          
          {/* Close Button */}
          <button
            onClick={onClose}
            className="p-2 bg-black/50 rounded-full backdrop-blur-sm hover:bg-black/70 transition-colors"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Route Type and Gradient Badges */}
        <div className="absolute top-2 left-2 flex flex-col gap-1">
          <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-purple-600/40 backdrop-blur-md text-white border border-purple-400/60 shadow-lg ring-1 ring-black/20">
            {route.routeType}
          </span>
          {(() => {
            const gradientLevel = getElevationGradientLevel(route.elevationGain, route.distance, route.routeType)
            const gradientColor = getElevationGradientColor(gradientLevel)
            const gradientLabel = getElevationGradientLabel(gradientLevel)
            return (
              <span 
                className="inline-flex items-center px-2 py-1 rounded text-xs font-medium text-white backdrop-blur-md shadow-lg ring-1 ring-black/20"
                style={{ backgroundColor: `${gradientColor}66`, border: `1px solid ${gradientColor}99` }}
              >
                {gradientLabel}
              </span>
            )
          })()}
          
          {/* Tags from embed config */}
          {embedConfig?.routes?.[route.firebaseRoute?.id]?.tags && embedConfig.routes[route.firebaseRoute.id].tags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {embedConfig.routes[route.firebaseRoute.id].tags.map((tag, index) => (
                <span 
                  key={index}
                  className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-600/40 backdrop-blur-md text-white border border-blue-400/60 shadow-lg ring-1 ring-black/20"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Image Navigation - only show if there are images */}
        {route.images.length > 1 && (
          <>
            <button 
              onClick={() => setCurrentImageIndex(prev => 
                prev === 0 ? route.images.length - 1 : prev - 1
              )}
              className="absolute left-2 top-1/2 transform -translate-y-1/2 p-2 bg-black/50 rounded-full backdrop-blur-sm hover:bg-black/70 transition-all"
            >
              <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button 
              onClick={() => setCurrentImageIndex(prev => 
                prev === route.images.length - 1 ? 0 : prev + 1
              )}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 p-2 bg-black/50 rounded-full backdrop-blur-sm hover:bg-black/70 transition-all"
            >
              <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
            
            {/* Image Indicators */}
            <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex space-x-1">
              {route.images.map((_, index) => (
                <div
                  key={index}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${ 
                    index === currentImageIndex ? 'bg-white' : 'bg-white/50'
                  }`}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Card Content */}
      <div className="p-4 space-y-3 relative z-10">
        {/* Title and Location */}
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-lg font-semibold text-white leading-tight">{route.name}</h3>
            {route.firebaseRoute?.visibility === 'members-only' && (
              <div className="flex items-center gap-1 px-2 py-0.5 bg-blue-500/10 backdrop-blur-md text-blue-400 rounded text-xs border border-blue-500/20">
                <Lock className="w-3 h-3" />
                <span>Members</span>
              </div>
            )}
            {route.firebaseRoute?.visibility === 'premium-members' && (
              <div className="flex items-center gap-1 px-2 py-0.5 bg-yellow-500/10 backdrop-blur-md text-yellow-400 rounded text-xs border border-yellow-500/20">
                <Key className="w-3 h-3" />
                <span>Premium</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-1 text-sm text-gray-400">
            <MapPin className="w-3 h-3" />
            {typeof route.location === 'string' 
              ? route.location 
              : route.location && typeof route.location === 'object'
                ? `${route.location.city || route.location.region || ''}, ${route.location.state || ''}, ${route.location.country || ''}`.replace(/^, |, $|, ,/g, '').replace(/^,\s*/, '')
                : 'Unknown Location'
            }
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-3">
          <div className="flex items-center gap-2">
            <Route className="w-4 h-4 text-emerald-400 flex-shrink-0" />
            <div>
              <div className="text-lg font-semibold text-white">{Math.round(route.distance)}</div>
              <div className="text-xs text-gray-400">km</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Mountain className="w-4 h-4 text-emerald-400 flex-shrink-0" />
            <div>
              <div className="text-lg font-semibold text-white">{route.elevationGain.toLocaleString()}</div>
              <div className="text-xs text-gray-400">m elev</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-emerald-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <div>
              <div className="text-lg font-semibold text-white">{route.unpavedPercentage || 0}%</div>
              <div className="text-xs text-gray-400">unpaved</div>
            </div>
          </div>
        </div>

        {/* Club and Creator Row */}
        <div className="flex items-center justify-between gap-4 pt-3 border-t border-white/[0.06]">
          {/* Club Section (Left) */}
          <div className="flex items-center gap-0.5 flex-1 min-w-0">
            {route.clubLogo ? (
              <img 
                src={route.clubLogo} 
                alt={route.organization}
                className="w-11 h-11 rounded-lg object-cover shadow-md"
              />
            ) : (
              <div className="w-11 h-11 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg flex items-center justify-center shadow-md">
                <span className="text-white font-bold text-base">
                  {route.organization.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
            <div className="min-w-0">
              <div className="text-xs font-medium text-white truncate">{route.organization}</div>
              <div className="flex items-center gap-1 text-xs text-gray-400">
                {isFollowingClub ? (
                  <>
                    <Check className="w-3 h-3" />
                    Member
                  </>
                ) : (
                  <>
                    <UserPlus className="w-3 h-3" />
                    Join club
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Creator Section (Right) */}
          <div className="flex items-center gap-2">
            {/* Check if this is a credited author by checking both possible attribution locations */}
            {((route.firebaseRoute?.attribution?.name && route.creator === route.firebaseRoute.attribution.name) ||
              (route.attribution?.name && route.creator === route.attribution.name)) ? (
              // For credited authors, always show initials placeholder
              <div className="w-8 h-8 bg-gradient-to-br from-emerald-300 via-emerald-500 to-emerald-800 rounded-full flex items-center justify-center">
                <span className="text-white font-semibold text-xs">
                  {route.creator.split(' ').map(word => word.charAt(0).toUpperCase()).join('').slice(0, 2)}
                </span>
              </div>
            ) : route.creatorPhoto ? (
              // For actual users with photos
              <img 
                src={route.creatorPhoto} 
                alt={route.creator}
                className="w-8 h-8 rounded-full object-cover"
              />
            ) : (
              // For actual users without photos
              <div className="w-8 h-8 bg-gray-600 rounded-full flex items-center justify-center">
                <span className="text-gray-300 font-semibold text-xs">
                  {route.creator.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
            <div>
              <div className="text-xs text-gray-400">
                {((route.firebaseRoute?.attribution?.name && route.creator === route.firebaseRoute.attribution.name) ||
                  (route.attribution?.name && route.creator === route.attribution.name)) ? 'Credited to' : 'Created by'}
              </div>
              <div className="text-xs text-gray-300">
                {((route.firebaseRoute?.attribution?.name && route.creator === route.firebaseRoute.attribution.name) ||
                  (route.attribution?.name && route.creator === route.attribution.name)) ? route.creator : `@${route.creator}`}
              </div>
            </div>
          </div>
        </div>

        {/* Route Links */}
        {route.firebaseRoute && (
          <div className="mt-3 space-y-2">
            {/* Default CYA Routes Link */}
            {(!embedConfig || embedConfig.links?.useDefaultLinks !== false || embedConfig.links?.showBothLinks) && onViewFullRoute && (
              <button
                onClick={() => {
                  onViewFullRoute(route.firebaseRoute)
                  onClose()
                }}
                className="w-full py-2 bg-emerald-500/20 backdrop-blur-md hover:bg-emerald-500/30 text-white font-medium rounded-lg transition-all border border-emerald-500/30"
              >
                View Full Route
              </button>
            )}
            
            {/* Custom Link - check new structure first, then old */}
            {(() => {
              const customLink = embedConfig?.routes?.[route.firebaseRoute.id]?.customLink || 
                               embedConfig?.links?.routeLinks?.[route.firebaseRoute.id]
              const showCustomLink = embedConfig?.links && !embedConfig.links.useDefaultLinks && customLink
              const showBothLinks = embedConfig?.links?.showBothLinks && customLink
              
              if (showCustomLink || showBothLinks) {
                return (
                  <a
                    href={customLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full py-2 bg-blue-500/20 backdrop-blur-md hover:bg-blue-500/30 text-white font-medium rounded-lg transition-all text-center border border-blue-500/30"
                  >
                    {embedConfig.links.customLinkText || 'View on Club Website'}
                  </a>
                )
              }
              return null
            })()}
          </div>
        )}
      </div>
    </div>
  )
}
